/**
 * Where tokens live: memory, and nowhere else.
 *
 * No access token, id token or refresh token is ever written to localStorage,
 * sessionStorage, a cookie, or the URL after the callback is consumed. Anything
 * persisted is readable by any script that manages to run on this origin, and a
 * refresh token in localStorage is a long-lived credential sitting in the exact
 * place an XSS payload looks first.
 *
 * The cost is that a reload loses the tokens. That is paid back by
 * `resumeSession`: Cognito keeps its own session cookie on its own domain, so a
 * silent trip through /oauth2/authorize returns a fresh code without the user
 * typing anything. What survives a reload is a boolean marker, not a credential
 * — the worst an attacker learns from it is that somebody was signed in.
 */

/** Not a token. Records only that a session existed, so reload can resume it. */
const RESUME_MARKER = 'vc.session.active';

/** The in-flight PKCE verifier and CSRF state, held only between redirects. */
const PENDING_KEY = 'vc.auth.pending';

export interface Tokens {
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken: string | null;
  /** Epoch milliseconds. */
  readonly expiresAt: number;
}

export interface PendingAuthorization {
  readonly verifier: string;
  readonly state: string;
  /** Where to land after sign-in, so a deep link survives the round trip. */
  readonly returnTo: string;
}

let tokens: Tokens | null = null;

export function getTokens(): Tokens | null {
  return tokens;
}

export function setTokens(next: Tokens): void {
  tokens = next;
  try {
    sessionStorage.setItem(RESUME_MARKER, '1');
  } catch {
    // Private browsing modes can refuse storage. Sign-in still works; only the
    // silent resume after a reload is lost, which degrades to a visible sign-in.
  }
}

export function clearTokens(): void {
  tokens = null;
  try {
    sessionStorage.removeItem(RESUME_MARKER);
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* nothing recoverable, and nothing sensitive left behind either */
  }
}

/**
 * True when this tab had a session before the page was reloaded — and consumes
 * the marker, so a silent resume is attempted at most once.
 *
 * Consuming matters because Cognito's hosted UI does not honour `prompt=none`
 * (ADR-013): when its session has expired it shows a sign-in form rather than
 * returning an error the callback could act on. If the marker survived that, the
 * tab would redirect to Cognito on every subsequent load and could never reach
 * Verydia's own `/login` — which is where the "invitation-only, ask an
 * administrator" context lives.
 *
 * One silent attempt, then fall back to our page. A successful resume sets the
 * marker again, so a working session still reloads silently every time.
 */
export function consumeResumeMarker(): boolean {
  try {
    const present = sessionStorage.getItem(RESUME_MARKER) === '1';
    sessionStorage.removeItem(RESUME_MARKER);
    return present;
  } catch {
    return false;
  }
}

/**
 * The verifier does touch sessionStorage, because it has to survive a full-page
 * redirect to Cognito and back. It is single-use, consumed the moment the
 * callback runs, and worthless without the matching code — which arrives in a
 * different place entirely.
 */
export function setPending(pending: PendingAuthorization): void {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
}

export function takePending(): PendingAuthorization | null {
  const raw = sessionStorage.getItem(PENDING_KEY);
  // Removed before it is even parsed: a replayed callback must not find it.
  sessionStorage.removeItem(PENDING_KEY);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as PendingAuthorization;
    if (typeof parsed.verifier !== 'string' || typeof parsed.state !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Treat a token as expired slightly early, so a request never races the clock. */
const EXPIRY_SKEW_MS = 60_000;

export function isExpired(value: Tokens, now: number = Date.now()): boolean {
  return now >= value.expiresAt - EXPIRY_SKEW_MS;
}
